// src/modules/myvault/views/AutomationExecutionLog.tsx

// What? Admin-only list of every automation run in the workspace with a
//       detail dialog that shows per-action results, timing, and row
//       snapshot. R-3 Phase 1 deliverable.
// Why? Admins need to see what actually fired when rules run -- trigger
//       inspection is the fastest way to diagnose "this didn't send" or
//       "that email went to the wrong address". The list filters by rule,
//       status, and date range (client-side range since the API doesn't
//       accept one yet -- Phase 1 keeps the contract read-only).
// How? Reads from /api/workspace/myvault/automation/executions with
//       server-side rule + status filters. Detail dialog does a GET on
//       the single-execution endpoint for the full actions_results array
//       rather than relying on the list payload (smaller lists load faster).

import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Stack,
  Table,
  Typography,
} from "@mui/joy";
import HistoryIcon from "@mui/icons-material/History";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import {
  useAutomationExecution,
  useAutomationExecutions,
  useAutomationRules,
  type AutomationExecution,
} from "../hooks/useAutomationRules";

const STATUS_COLORS: Record<
  AutomationExecution["status"],
  "success" | "danger" | "warning" | "neutral"
> = {
  pending: "neutral",
  condition_not_met: "neutral",
  no_matches: "neutral",
  skipped: "neutral",
  success: "success",
  partial_failure: "warning",
  failure: "danger",
  running: "neutral",
};

function formatExecutionStatus(status: string): string {
  switch (status) {
    case "condition_not_met":
      return "Condition not met";
    case "no_matches":
      return "No matches";
    case "partial_failure":
      return "Partial failure";
    default:
      return status.replace(/_/g, " ");
  }
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function ActionResultsTable({
  results,
}: {
  results: Record<string, unknown>[];
}) {
  if (!results.length) {
    return (
      <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
        No action results recorded.
      </Typography>
    );
  }
  return (
    <Sheet
      variant="outlined"
      sx={{ borderRadius: "sm", overflow: "auto", maxHeight: 360 }}
    >
      <Table size="sm">
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th style={{ width: 150 }}>Type</th>
            <th style={{ width: 90 }}>Status</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r, idx) => {
            const status =
              typeof r.status === "string" ? r.status : Boolean(r.success) ? "success" : "failure";
            const success = Boolean(r.success);
            const type = typeof r.type === "string" ? r.type : "-";
            const isNeutralStatus = ["skipped", "condition_not_met", "no_matches"].includes(
              status,
            );
            const chipColor =
              isNeutralStatus
                ? "neutral"
                : success
                  ? "success"
                  : "danger";
            const chipLabel =
              isNeutralStatus ? formatExecutionStatus(status) : success ? "ok" : "failed";
            return (
              <tr key={idx}>
                <td>
                  <Typography level="body-xs">
                    {typeof r.index === "number" ? r.index : idx}
                  </Typography>
                </td>
                <td>
                  <Typography level="body-xs" sx={{ fontFamily: "monospace" }}>
                    {type}
                  </Typography>
                </td>
                <td>
                  <Chip
                    size="sm"
                    color={chipColor}
                    variant="soft"
                  >
                    {chipLabel}
                  </Chip>
                </td>
                <td>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      fontSize: "11px",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      color: chipColor === "danger" ? "danger.600" : "text.secondary",
                    }}
                  >
                    {JSON.stringify(
                      {
                        ...r,
                        type: undefined,
                        success: undefined,
                        index: undefined,
                      },
                      null,
                      2,
                    )}
                  </Box>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Sheet>
  );
}

function ExecutionDetailDialog({
  workspaceId,
  execId,
  ruleNameById,
  onClose,
}: {
  workspaceId: string;
  execId: string | null;
  ruleNameById: Map<string, string>;
  onClose: () => void;
}) {
  const { execution, loading, error } = useAutomationExecution(
    workspaceId,
    execId,
  );

  return (
    <Modal open={!!execId} onClose={onClose}>
      <ModalDialog
        layout="center"
        sx={{
          width: "min(900px, 95vw)",
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <ModalClose />
        <Typography level="title-lg">Execution detail</Typography>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert color="danger">{error}</Alert>
        ) : execution ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Box>
                <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                  Rule
                </Typography>
                <Typography level="body-sm">
                  {ruleNameById.get(execution.rule_id) || execution.rule_id}
                </Typography>
              </Box>
              <Box>
                <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                  Status
                </Typography>
                <Chip
                  size="sm"
                  color={STATUS_COLORS[execution.status] || "neutral"}
                  variant="soft"
                >
                  {formatExecutionStatus(execution.status)}
                </Chip>
              </Box>
              <Box>
                <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                  Trigger
                </Typography>
                <Typography level="body-sm">
                  {execution.trigger_type}
                </Typography>
              </Box>
              <Box>
                <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                  Triggered by
                </Typography>
                <Typography level="body-sm" sx={{ fontFamily: "monospace" }}>
                  {execution.triggered_by}
                </Typography>
              </Box>
              <Box>
                <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                  Started
                </Typography>
                <Typography level="body-sm">
                  {formatDateTime(execution.started_at)}
                </Typography>
              </Box>
              <Box>
                <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                  Duration
                </Typography>
                <Typography level="body-sm">
                  {execution.duration_ms !== null
                    ? `${execution.duration_ms} ms`
                    : "-"}
                </Typography>
              </Box>
              <Box>
                <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                  Row
                </Typography>
                <Typography level="body-sm" sx={{ fontFamily: "monospace" }}>
                  {execution.row_id || "-"}
                </Typography>
              </Box>
            </Stack>

            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>
                Actions
              </Typography>
              <ActionResultsTable results={execution.actions_results} />
            </Box>

            {(execution.old_values || execution.new_values) && (
              <Box>
                <Typography level="title-sm" sx={{ mb: 1 }}>
                  Row snapshot
                </Typography>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                  <Sheet
                    variant="soft"
                    sx={{ flex: 1, p: 1.5, borderRadius: "sm" }}
                  >
                    <Typography
                      level="body-xs"
                      sx={{ color: "text.tertiary", mb: 0.5 }}
                    >
                      Before
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        fontSize: "11px",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        maxHeight: 240,
                        overflow: "auto",
                      }}
                    >
                      {JSON.stringify(execution.old_values ?? {}, null, 2)}
                    </Box>
                  </Sheet>
                  <Sheet
                    variant="soft"
                    sx={{ flex: 1, p: 1.5, borderRadius: "sm" }}
                  >
                    <Typography
                      level="body-xs"
                      sx={{ color: "text.tertiary", mb: 0.5 }}
                    >
                      After
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        fontSize: "11px",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        maxHeight: 240,
                        overflow: "auto",
                      }}
                    >
                      {JSON.stringify(execution.new_values ?? {}, null, 2)}
                    </Box>
                  </Sheet>
                </Stack>
              </Box>
            )}
          </Stack>
        ) : null}
      </ModalDialog>
    </Modal>
  );
}

export default function AutomationExecutionLog() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  const ruleId = searchParams.get("rule_id") || "";
  const status = searchParams.get("status") || "";

  const [openExecId, setOpenExecId] = useState<string | null>(null);

  const filters = useMemo(
    () => ({
      ruleId: ruleId || undefined,
      status: status || undefined,
      limit: 100,
    }),
    [ruleId, status],
  );

  const { executions, loading, error, refresh } = useAutomationExecutions(
    workspaceId,
    filters,
  );
  const { rules } = useAutomationRules(workspaceId);

  const ruleNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const rule of rules) {
      map.set(rule.id, rule.name);
    }
    return map;
  }, [rules]);

  const updateFilter = (key: "rule_id" | "status", value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
  };

  if (!workspaceId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="warning">Please select a workspace first.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Stack spacing={3}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          sx={{ gap: 2 }}
        >
          <Box>
            <Typography
              level="h2"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <HistoryIcon sx={{ color: "primary.500" }} />
              Execution log
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
              Every automation run in this workspace, newest first.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<ArrowBackIcon />}
              onClick={() => navigate("..")}
            >
              Back to rules
            </Button>
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<RefreshIcon />}
              onClick={() => refresh()}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>

        <Card variant="outlined">
          <CardContent>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ sm: "flex-end" }}
            >
              <FormControl sx={{ minWidth: 260 }}>
                <FormLabel>Rule</FormLabel>
                <Select
                  size="sm"
                  value={ruleId}
                  onChange={(_, val) =>
                    updateFilter("rule_id", (val as string) || "")
                  }
                  placeholder="All rules"
                >
                  <Option value="">All rules</Option>
                  {rules.map((r) => (
                    <Option key={r.id} value={r.id}>
                      {r.name}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }}>
                <FormLabel>Status</FormLabel>
                <Select
                  size="sm"
                  value={status}
                  onChange={(_, val) =>
                    updateFilter("status", (val as string) || "")
                  }
                  placeholder="Any status"
                >
                  <Option value="">Any status</Option>
                  <Option value="pending">Pending</Option>
                  <Option value="condition_not_met">Condition not met</Option>
                  <Option value="no_matches">No matches</Option>
                  <Option value="skipped">Skipped</Option>
                  <Option value="success">Success</Option>
                  <Option value="partial_failure">Partial failure</Option>
                  <Option value="failure">Failure</Option>
                  <Option value="running">Running</Option>
                </Select>
              </FormControl>
            </Stack>
          </CardContent>
        </Card>

        {error && <Alert color="danger">{error}</Alert>}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : executions.length === 0 ? (
          <Sheet
            variant="soft"
            sx={{ borderRadius: "md", p: 6, textAlign: "center" }}
          >
            <Typography level="title-md" sx={{ mb: 1 }}>
              No executions match these filters
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
              Clear the filters or wait for a rule to fire.
            </Typography>
          </Sheet>
        ) : (
          <Sheet
            variant="outlined"
            sx={{ borderRadius: "sm", overflow: "hidden" }}
          >
            <Table size="sm" hoverRow stickyHeader>
              <thead>
                <tr>
                  <th style={{ width: "22%" }}>Started</th>
                  <th style={{ width: "26%" }}>Rule</th>
                  <th style={{ width: "16%" }}>Status</th>
                  <th style={{ width: "16%" }}>Trigger</th>
                  <th style={{ width: "10%" }}>Actions</th>
                  <th style={{ width: "10%" }}>Duration</th>
                </tr>
              </thead>
              <tbody>
                {executions.map((exe) => (
                  <tr
                    key={exe.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => setOpenExecId(exe.id)}
                  >
                    <td>
                      <Typography level="body-sm">
                        {formatDateTime(exe.started_at || exe.triggered_at)}
                      </Typography>
                    </td>
                    <td>
                      <Typography level="body-sm">
                        {ruleNameById.get(exe.rule_id) || exe.rule_id}
                      </Typography>
                    </td>
                    <td>
                      <Chip
                        size="sm"
                        color={STATUS_COLORS[exe.status] || "neutral"}
                        variant="soft"
                      >
                        {formatExecutionStatus(exe.status)}
                      </Chip>
                    </td>
                    <td>
                      <Typography level="body-xs">
                        {exe.trigger_type}
                      </Typography>
                    </td>
                    <td>
                      <Typography level="body-xs">
                        {exe.actions_results?.length ?? 0}
                      </Typography>
                    </td>
                    <td>
                      <Typography level="body-xs">
                        {exe.duration_ms !== null
                          ? `${exe.duration_ms} ms`
                          : "-"}
                      </Typography>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Sheet>
        )}
      </Stack>

      <ExecutionDetailDialog
        workspaceId={workspaceId}
        execId={openExecId}
        ruleNameById={ruleNameById}
        onClose={() => setOpenExecId(null)}
      />
    </Box>
  );
}
